/**
 * useArticlePathMode: bridge between the article path-mode services and React (Task 31, decisions #10/#11).
 *
 * - The server owns the state (customDomains article doc + the mode switch job); this hook only reads it and DRIVES the
 *   job: after `set`/`clear` it calls `step` until the job leaves `running`.
 * - A running job found on load is resumed (Review Focus 3): closing the tab leaves the job running, the next mount
 *   continues it. Unmounting stops the loop, it never cancels the job.
 * - "Verify & enable" is the server `set` (decision #10): there is no separate UI dry verify.
 * - One request at a time (`inFlight`): a second action while one runs sends nothing (AC-065).
 * - Input the shared validator refuses never reaches the server (AC-073).
 */
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { PUBLIC_BASE_URL_MESSAGES, validatePublicBaseUrl } from '@modules/custom-domains/utils/publicBaseUrl';
import { customDomainApi } from '@modules/custom-domains/services/customDomainApi';
import { isLocalBuilderApi } from '@modules/custom-domains/services/articlePathModeEnv';
import { loadLiveArticles } from '@modules/custom-domains/services/articleLiveAliases';
import { pathModeViewOf, type ArticlePathModeView } from '@modules/custom-domains/hooks/articlePathModeView';
import type { LiveArticle } from '@modules/firebase-core';
import type { ArticleModeSwitchJob, ArticlePathModeState } from '@modules/custom-domains/types/customDomain.types';

export type ArticlePathModeBusy = 'verifying' | 'switching' | 'disabling' | 'home';

export interface ArticlePathModeError {
  code: string;
  message: string;
}

export interface UseArticlePathModeOptions {
  /** Accept http://localhost / 127.0.0.1 base URLs. Default: the frontend talks to a local builder. */
  allowLocalHttp?: boolean;
  /** Wait before the next `step` when a step made no progress (another stepper holds the batch). Default 1500. */
  idleDelayMs?: number;
}

export interface UseArticlePathModeResult {
  state: ArticlePathModeState | null;
  /** True until the first state read finishes. */
  loading: boolean;
  busy: ArticlePathModeBusy | null;
  /** Last server (or generic) failure of an action; cleared by the next action. */
  error: ArticlePathModeError | null;
  /** Last client-side validation message (set by `validateInput` / a refused `verifyAndEnable`). */
  clientError: string | null;
  /** The project's live (published, not retired) articles with id + title: Home page options, report titles (R-17, C-6). */
  liveArticles: LiveArticle[];
  /** Presentation reading of `state` (running switch, active base, failures of the last switch). */
  view: ArticlePathModeView;
  /**
   * Live typing check: returns the message (null = valid or blank) AND records it as `clientError`.
   * Call it from an event handler (onChange), not during render.
   */
  validateInput(value: string): string | null;
  verifyAndEnable(value: string): Promise<void>;
  disable(): Promise<void>;
  /** `null` clears the home page. */
  setHome(alias: string | null): Promise<void>;
  /** Re-read the state (and resume a running job). */
  refresh(): Promise<void>;
}

const DEFAULT_IDLE_DELAY_MS = 1500;
export const GENERIC_PATH_MODE_ERROR: ArticlePathModeError = { code: 'server_error', message: 'Something went wrong — try again.' };

/** 4xx codes of path mode whose server message is written for the customer (Spec Issue #24). */
const CUSTOMER_MESSAGE_CODES: ReadonlySet<string> = new Set([
  'invalid_base_url', 'article_domain_required', 'base_url_unreachable', 'base_url_timeout', 'base_url_not_proxied',
  'base_url_wrong_project', 'mode_switch_in_progress', 'mode_switch_not_found', 'invalid_request', 'rate_limited',
]);

interface ProblemLike {
  code?: unknown;
  status?: unknown;
  message?: unknown;
  details?: { errorCode?: unknown; jobId?: unknown };
}

/** Known 4xx codes and 402 show the server text; everything else (5xx, network, unknown) is the generic one (AC-080). */
export function toPathModeError(error: unknown): ArticlePathModeError {
  if (error instanceof SwitchJobLostError) return { code: 'mode_switch_lost', message: error.message };
  const problem = (error ?? {}) as ProblemLike;
  const { status, message } = problem;
  if (typeof status !== 'number' || typeof message !== 'string' || message === '') return GENERIC_PATH_MODE_ERROR;
  if (status === 402) {
    const code = typeof problem.details?.errorCode === 'string' ? problem.details.errorCode : 'billing_capability_required';
    return { code, message };
  }
  if (status >= 400 && status < 500 && typeof problem.code === 'string' && CUSTOMER_MESSAGE_CODES.has(problem.code)) {
    return { code: problem.code, message };
  }
  return GENERIC_PATH_MODE_ERROR;
}

/** What the user asked for; a 409 that resumes someone else's job is only a success when the job does the same. */
type RunIntent = { kind: 'set'; publicBaseUrl: string } | { kind: 'clear' } | { kind: 'home' };

/** A step answered without the job it stepped: never end silently. */
class SwitchJobLostError extends Error {
  constructor() {
    super('The path-mode switch changed while it was running. Reload this page to see where your articles are served.');
    this.name = 'SwitchJobLostError';
  }
}

const matchesIntent = (job: ArticleModeSwitchJob, intent: RunIntent | undefined): boolean => {
  if (!intent) return true;
  if (intent.kind === 'set') return job.kind === 'set' && job.publicBaseUrl === intent.publicBaseUrl;
  if (intent.kind === 'clear') return job.kind === 'clear';
  return false;
};

const isSwitchInProgress = (error: unknown): boolean => {
  const problem = (error ?? {}) as ProblemLike;
  return problem.status === 409 && problem.code === 'mode_switch_in_progress';
};

const isRunning = (job: ArticleModeSwitchJob | null | undefined): job is ArticleModeSwitchJob => job?.status === 'running';

/** Changes whenever a step moved the job; equal keys = the step made no progress. */
const progressKey = (job: ArticleModeSwitchJob): string =>
  [job.jobId, job.status, job.pending?.length ?? 0, job.results?.length ?? 0, job.inFlight?.articleIds?.length ?? 0, job.finishingAt ?? ''].join('|');

const sleep = (ms: number) => new Promise<void>((resolve) => setTimeout(resolve, ms));

export function useArticlePathMode(projectId: string, options: UseArticlePathModeOptions = {}): UseArticlePathModeResult {
  const allowLocalHttp = options.allowLocalHttp ?? isLocalBuilderApi();
  const idleDelayMs = options.idleDelayMs ?? DEFAULT_IDLE_DELAY_MS;

  const [state, setState] = useState<ArticlePathModeState | null>(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState<ArticlePathModeBusy | null>(null);
  const [error, setError] = useState<ArticlePathModeError | null>(null);
  const [clientError, setClientError] = useState<string | null>(null);
  const [liveArticles, setLiveArticles] = useState<LiveArticle[]>([]);

  /** Bumped on project change and unmount: results of an older epoch are dropped and its loop stops. */
  const epoch = useRef(0);
  const inFlight = useRef(false);
  const articleHost = state?.articleHost ?? null;

  /** Steps the job until it is no longer running (the only owner of the loop). */
  const drive = useCallback(async (first: ArticlePathModeState, mine: number): Promise<void> => {
    let current = first;
    while (epoch.current === mine && isRunning(current.job)) {
      const job = current.job;
      setBusy(job.kind === 'clear' ? 'disabling' : 'switching');
      const before = progressKey(job);
      const next = await customDomainApi.stepBaseUrl(projectId, job.jobId);
      if (epoch.current !== mine) return;
      setState(next);
      if (!next.job || next.job.jobId !== job.jobId) throw new SwitchJobLostError();
      if (isRunning(next.job) && progressKey(next.job) === before) await sleep(idleDelayMs);
      current = next;
    }
  }, [projectId, idleDelayMs]);

  /** Runs one request under the in-flight lock, then drives a job it started or found. */
  const run = useCallback(async (
    initialBusy: ArticlePathModeBusy | null,
    request: () => Promise<ArticlePathModeState>,
    intent?: RunIntent,
  ): Promise<void> => {
    if (inFlight.current) return;
    inFlight.current = true;
    const mine = epoch.current;
    setError(null);
    setBusy(initialBusy);
    try {
      const next = await request();
      if (epoch.current !== mine) return;
      setState(next);
      await drive(next, mine);
    } catch (failure) {
      if (epoch.current !== mine) return;
      try {
        // Another tab/stepper already runs the switch: show its progress instead of an error.
        if (isSwitchInProgress(failure)) {
          const fresh = await customDomainApi.getArticleSite(projectId);
          if (epoch.current !== mine) return;
          setState(fresh);
          if (isRunning(fresh.job)) {
            await drive(fresh, mine);
            // Resuming is a success only when that job is what the user asked for (same kind and base URL).
            if (epoch.current === mine && !matchesIntent(fresh.job, intent)) setError(toPathModeError(failure));
            return;
          }
        }
        setError(toPathModeError(failure));
      } catch (secondary) {
        if (epoch.current === mine) setError(toPathModeError(secondary));
      }
    } finally {
      if (epoch.current === mine) {
        inFlight.current = false;
        setBusy(null);
      }
    }
  }, [drive, projectId]);

  const loadArticles = useCallback(async (mine: number): Promise<void> => {
    try {
      const articles = await loadLiveArticles(projectId);
      if (epoch.current === mine) setLiveArticles(articles);
    } catch {
      // Non-fatal: the Home page select stays on what it had; the server still validates any alias.
    }
  }, [projectId]);

  const reload = useCallback(async (): Promise<void> => {
    if (inFlight.current) return;
    const mine = epoch.current;
    const articles = loadArticles(mine);
    await run(null, async () => {
      try {
        return await customDomainApi.getArticleSite(projectId);
      } finally {
        if (epoch.current === mine) setLoading(false);
      }
    });
    await articles;
  }, [loadArticles, projectId, run]);

  useEffect(() => {
    epoch.current += 1;
    inFlight.current = false;
    setState(null);
    setLoading(true);
    setBusy(null);
    setError(null);
    setClientError(null);
    setLiveArticles([]);
    void reload();
    return () => {
      // Unmount (tab closed): stop driving; the job itself keeps running and the next mount resumes it.
      epoch.current += 1;
      inFlight.current = false;
    };
    // `reload` is rebuilt only when projectId changes.
  }, [reload]);

  const validateInput = useCallback((value: string): string | null => {
    if (value.trim() === '') {
      setClientError(null);
      return null;
    }
    const result = validatePublicBaseUrl(value, { allowLocalHttp, articleHost });
    const message = result.ok ? null : result.message;
    setClientError(message);
    return message;
  }, [allowLocalHttp, articleHost]);

  const verifyAndEnable = useCallback(async (value: string): Promise<void> => {
    if (inFlight.current) return;
    if (value.trim() === '') {
      setClientError(PUBLIC_BASE_URL_MESSAGES.empty);
      return;
    }
    const result = validatePublicBaseUrl(value, { allowLocalHttp, articleHost });
    if (!result.ok) {
      setClientError(result.message);
      return;
    }
    setClientError(null);
    await run('verifying', () => customDomainApi.setBaseUrl(projectId, result.value), { kind: 'set', publicBaseUrl: result.value });
  }, [allowLocalHttp, articleHost, projectId, run]);

  const disable = useCallback(
    () => run('disabling', () => customDomainApi.clearBaseUrl(projectId), { kind: 'clear' }),
    [projectId, run],
  );

  const setHome = useCallback(
    (alias: string | null) => run(
      'home',
      () => (alias === null ? customDomainApi.clearHome(projectId) : customDomainApi.setHome(projectId, alias)),
      { kind: 'home' },
    ),
    [projectId, run],
  );

  const view = useMemo(() => pathModeViewOf(state), [state]);

  return { state, loading, busy, error, clientError, liveArticles, view, validateInput, verifyAndEnable, disable, setHome, refresh: reload };
}
